import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { GUIDES } from "../data/content";

export default function GuideDetail() {
  const { slug } = useParams();
  const guide = GUIDES.find((g) => g.slug === slug);
  if (!guide) return <Navigate to="/guides" replace />;

  const idx = GUIDES.findIndex((g) => g.slug === slug);
  const next = GUIDES[(idx + 1) % GUIDES.length];

  return (
    <>
      <SEO
        title={guide.title}
        description={guide.excerpt}
        path={`/guides/${guide.slug}`}
        image={guide.image}
      />

      {/* HERO */}
      <section className="relative flex min-h-[54vh] items-end overflow-hidden">
        <img src={guide.image} alt={guide.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-3xl px-5 pb-14 pt-32 lg:px-8">
          <Link
            to="/guides"
            data-testid="guide-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> All guides
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy">
              {guide.category}
            </span>
            <h1 className="mt-4 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl">
              {guide.title}
            </h1>
            <div className="mt-4 flex items-center gap-1.5 text-sm text-slate-300">
              <Clock className="h-4 w-4" /> {guide.readTime}
            </div>
          </motion.div>
        </div>
      </section>

      {/* BODY */}
      <article className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
        <Reveal>
          <p className="font-serif-accent text-2xl leading-relaxed text-navy">
            {guide.excerpt}
          </p>
        </Reveal>

        <div className="mt-12 space-y-12">
          {guide.sections.map((s, i) => (
            <Reveal key={s.heading} delay={0.04}>
              <section>
                <Overline>{`0${i + 1}`}</Overline>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy">
                  {s.heading}
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-slate-600">{s.body}</p>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl bg-muted p-8 text-center">
            <h3 className="font-display text-xl font-bold text-navy">
              Ready to put this into action?
            </h3>
            <p className="mt-2 text-slate-600">
              Browse curated escapes and send a zero-pressure inquiry.
            </p>
            <Link
              to="/packages"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground btn-glow transition-transform duration-200 hover:scale-105"
            >
              Explore Packages <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <Link
            to={`/guides/${next.slug}`}
            data-testid="guide-next-link"
            className="group mt-10 flex items-center justify-between rounded-3xl card-3d p-6"
          >
            <span>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Next guide
              </span>
              <span className="mt-1 block font-display text-lg font-bold text-navy">
                {next.title}
              </span>
            </span>
            <ArrowRight className="h-6 w-6 text-primary transition-transform duration-200 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </article>
    </>
  );
}
